import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/utils-format";
import { Toggle } from "@/components/admin/AdminUI";

const STATUS_LABELS = { draft: "Borrador", scheduled: "Programado", published: "Publicado", archived: "Archivado" };

export default function FeaturedAdmin() {
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    try {
      const { data } = await api.put(`/admin/articles/${a.id}/featured`, { featured: !a.featured });
      setArticles((prev) => prev.map((x) => (x.id === a.id ? { ...x, featured: data.featured } : x)));
      toast.success(data.featured ? "Añadido a destacados" : "Quitado de destacados");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = articles.filter((a) => a.title.toLowerCase().includes(search.toLowerCase()));
  const featured = filtered.filter((a) => a.featured);
  const rest = filtered.filter((a) => !a.featured);

  const Row = ({ a }) => (
    <div className="flex items-center gap-4 bg-white border border-sunset-border p-3" data-testid={`featured-row-${a.id}`}>
      <div className="w-16 h-16 bg-sunset-border shrink-0 overflow-hidden">
        {a.cover_image && <img src={mediaUrl(a.cover_image)} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {a.featured && <Star size={13} className="text-sunset-accent fill-sunset-accent shrink-0" />}
          <p className="font-medium truncate">{a.title}</p>
        </div>
        <p className="text-xs text-sunset-muted mt-0.5">
          {a.category?.name || "Sin categoría"} · {STATUS_LABELS[a.status]} · {formatDate(a.published_at || a.created_at)}
        </p>
      </div>
      <Link to={`/admin/articulos/${a.id}`} className="p-2 text-sunset-muted hover:text-sunset-accent" title="Editar artículo"><ExternalLink size={15} /></Link>
      <Toggle checked={!!a.featured} onChange={() => toggle(a)} />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <header className="mb-2">
        <h1 className="font-serif text-4xl tracking-tight">Historias Destacadas</h1>
        <p className="text-sunset-muted text-sm mt-2">Elige qué artículos se destacan en la portada. El primero de la lista aparece como la <strong>Historia Destacada</strong> principal.</p>
      </header>

      <div className="relative my-6 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sunset-muted" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar artículo…"
          className="w-full bg-white border border-sunset-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-sunset-accent" />
      </div>

      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Star size={16} className="text-sunset-accent fill-sunset-accent" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-sunset-ink">Destacados actuales ({featured.length})</h2>
        </div>
        {featured.length === 0 ? (
          <p className="text-sunset-muted text-sm bg-white border border-dashed border-sunset-border p-6 text-center">Aún no hay historias destacadas. Activa alguna abajo.</p>
        ) : (
          <div className="space-y-3">{featured.map((a) => <Row key={a.id} a={a} />)}</div>
        )}
      </section>

      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-sunset-muted mb-4">Otros artículos</h2>
        <div className="space-y-3">{rest.map((a) => <Row key={a.id} a={a} />)}</div>
        {rest.length === 0 && <p className="text-sunset-muted text-sm py-6 text-center">No hay más artículos.</p>}
      </section>
    </div>
  );
}
